import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { QueryMessage } from '../../core/types/workspaceTypes';
import { buildOutputLines } from '../utils/outputPanelUtils';
import { OutputPanel } from './OutputPanel';

interface StackedOutputSectionProps {
  messages?: QueryMessage[];
  resultSetCount: number;
  onSelectResultSet?: (index: number) => void;
}

const PREVIEW_LINE_COUNT = 3;

function previewText(
  lines: ReturnType<typeof buildOutputLines>,
): string {
  const out: string[] = [];
  for (const line of lines) {
    if (out.length >= PREVIEW_LINE_COUNT) break;
    if (line.kind === 'placeholder') {
      out.push(`[Result ${line.index + 1}]`);
    } else {
      const trimmed = line.message.content.trim();
      if (trimmed.length > 0) out.push(trimmed);
    }
  }
  return out.join('\n');
}

export function StackedOutputSection({
  messages,
  resultSetCount,
  onSelectResultSet,
}: StackedOutputSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const lines = useMemo(
    () => buildOutputLines(messages, resultSetCount),
    [messages, resultSetCount],
  );

  if (lines.length === 0) return null;

  const preview = previewText(lines);

  return (
    <div
      className="border border-border rounded-md overflow-hidden"
      data-testid="stacked-output-section"
    >
      <button
        type="button"
        onClick={() => setIsExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-muted/50 hover:bg-muted/70 transition-colors text-left"
        data-testid="stacked-output-expand-toggle"
        aria-expanded={isExpanded}
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
        <span className="text-sm font-medium">Output</span>
      </button>
      {!isExpanded && (
        <div
          className="px-3 py-2 font-mono text-xs whitespace-pre-wrap text-muted-foreground"
          data-testid="stacked-output-preview"
        >
          {preview}
        </div>
      )}
      {isExpanded && (
        <div data-testid="stacked-output-full">
          <OutputPanel
            messages={messages}
            resultSetCount={resultSetCount}
            onSelectResultSet={onSelectResultSet}
            testId="stacked-output-full-pane"
          />
        </div>
      )}
    </div>
  );
}
